/**
 * TenantsAccessView — Identity záložka "Tenanti"
 * Strom tenantů vlevo, detail přístupů vpravo + profil (kontakty, helpdesk-ready).
 *
 * Vložit do Entities.tsx jako nový view. Data výhradně MCP (mesh.tenants).
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { mcpCall } from '../lib/mcp';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { EntityAvatar } from '../components/ui/EntityAvatar';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { EmptyState } from '../components/ui/EmptyState';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { TenantTree } from '../components/TenantTree';
import type { TenantFlat } from '../components/TenantTree';
import { Building2, Key, Mail, Phone, Plus, Server, Shield, Users } from 'lucide-react';

interface GroupRow {
  id: string;
  name: string;
  description: string | null;
  metadata: Record<string, unknown>;
  member_count: number;
  tenant_id: string | null;
  tenant_slug: string | null;
}

interface TenantSummary {
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  group_count: number;
  member_count: number;
  key_count: number;
}

interface TenantMember {
  id: string;
  type: string;
  label: string;
  enabled: boolean;
  metadata: Record<string, unknown> | null;
}

export function TenantsAccessView() {
  const { t } = useTranslation();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  // Tenants
  const { data: tenantsData } = useQuery({
    queryKey: ['mesh-tenants-tree'],
    queryFn: () => mcpCall<{ ok: boolean; tenants: TenantFlat[] }>('mesh.tenants', { action: 'list' }),
    staleTime: 30_000,
  });
  const tenants = tenantsData?.tenants ?? [];

  // Summaries — MCP-first (identity.tenant_summary)
  const { data: summaryData } = useQuery({
    queryKey: ['tenant-summaries'],
    queryFn: () =>
      mcpCall<{ ok: boolean; summaries: TenantSummary[] }>('identity.tenant_summary'),
    staleTime: 30_000,
  });
  const summaryMap: Record<
    string,
    { group_count: number; member_count: number; key_count: number }
  > = {};
  for (const s of summaryData?.summaries ?? []) {
    summaryMap[s.tenant_id] = {
      group_count: s.group_count,
      member_count: s.member_count,
      key_count: s.key_count,
    };
  }

  const selectedTenant = tenants.find((tn) => tn.id === selectedTenantId);

  return (
    <div className='space-y-4'>
      <p className='text-xs text-gray-500'>
        {t('tenantsAccess.intro')}
      </p>
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
        {/* Left: Tree */}
        <Card>
          <CardHeader>
            <CardTitle className='text-sm'>{t('tenantsAccess.tenantTreeTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            {tenants.length === 0
              ? (
                <EmptyState
                  title={t('tenantsAccess.noTenants')}
                  description={t('tenantsAccess.noTenantsDesc')}
                />
              )
              : (
                <TenantTree
                  tenants={tenants}
                  selected={selectedTenantId}
                  onSelect={setSelectedTenantId}
                  summaries={summaryMap}
                />
              )}
          </CardContent>
        </Card>

        {/* Right: Detail */}
        <div className='lg:col-span-2 space-y-4'>
          {selectedTenantId === null
            ? (
              <Card>
                <CardContent className='py-8'>
                  <EmptyState
                    icon={<Shield size={28} />}
                    title='MaxCloud'
                    description={t('tenantsAccess.rootOrgDesc')}
                  />
                </CardContent>
              </Card>
            )
            : selectedTenant
            ? <TenantDetailPanel tenant={selectedTenant} summary={summaryMap[selectedTenantId]} />
            : (
              <Card>
                <CardContent className='py-8'>
                  <EmptyState title={t('tenantsAccess.tenantNotFound')} />
                </CardContent>
              </Card>
            )}
        </div>
      </div>
    </div>
  );
}

type TenantProfileForm = {
  legal_name: string;
  ico: string;
  dic: string;
  address: string;
  city: string;
  zip: string;
  country: string;
  website: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  tech_name: string;
  tech_email: string;
  tech_phone: string;
  billing_email: string;
  notes: string;
  helpdesk_node_id: string;
  helpdesk_url: string;
  helpdesk_project_key: string;
  helpdesk_external_id: string;
};

const emptyProfile = (): TenantProfileForm => ({
  legal_name: '',
  ico: '',
  dic: '',
  address: '',
  city: '',
  zip: '',
  country: 'CZ',
  website: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  tech_name: '',
  tech_email: '',
  tech_phone: '',
  billing_email: '',
  notes: '',
  helpdesk_node_id: '',
  helpdesk_url: '',
  helpdesk_project_key: '',
  helpdesk_external_id: '',
});

function TenantProfileCard({ tenant }: { tenant: TenantFlat }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<TenantProfileForm>(emptyProfile);
  const [displayName, setDisplayName] = useState(tenant.name);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['tenant-profile', tenant.slug],
    queryFn: () =>
      mcpCall<{
        ok: boolean;
        tenant?: {
          name: string;
          profile: Partial<TenantProfileForm>;
          le_email?: string | null;
        };
        error?: string;
      }>('mesh.tenants', { action: 'profile_get', slug: tenant.slug }),
    staleTime: 15_000,
  });

  useEffect(() => {
    setDisplayName(tenant.name);
  }, [tenant.slug, tenant.name]);

  useEffect(() => {
    const p = data?.tenant?.profile ?? {};
    const base = emptyProfile();
    for (const k of Object.keys(base) as (keyof TenantProfileForm)[]) {
      if (p[k] != null) base[k] = String(p[k] ?? '');
    }
    setForm(base);
    if (data?.tenant?.name) setDisplayName(data.tenant.name);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () =>
      mcpCall<{ ok: boolean; error?: string }>('mesh.tenants', {
        action: 'profile_set',
        slug: tenant.slug,
        name: displayName.trim() || tenant.name,
        profile: form,
      }),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('tenantsAccess.profileSaveFail', 'Uložení profilu selhalo'));
        return;
      }
      toast.success(t('tenantsAccess.profileSaved', 'Profil tenanta uložen'));
      void qc.invalidateQueries({ queryKey: ['tenant-profile', tenant.slug] });
      void qc.invalidateQueries({ queryKey: ['mesh-tenants-tree'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const set = (k: keyof TenantProfileForm, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  if (isLoading) {
    return (
      <Card>
        <CardContent className='py-6 text-xs text-gray-500'>{t('common.loading')}</CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between w-full gap-2'>
          <CardTitle className='text-sm'>
            <Building2 size={14} className='inline mr-1' />
            {t('tenantsAccess.profileTitle', 'Karta tenanta')}
          </CardTitle>
          <Button
            variant='primary'
            size='sm'
            loading={saveMut.isPending || isFetching}
            onClick={() => saveMut.mutate()}
          >
            {t('common.save', 'Uložit')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className='space-y-4'>
        <p className='text-xs text-gray-500'>
          {t(
            'tenantsAccess.profileHint',
            'Základní údaje a kontakty (MCP mesh.tenants profile_*). Později propojení s helpdeskem na ACM nodu.',
          )}
        </p>

        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
          <div className='sm:col-span-2'>
            <Label>{t('tenantsAccess.displayName', 'Zobrazovaný název')}</Label>
            <Input size='sm' value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <div className='sm:col-span-2'>
            <Label>{t('tenantsAccess.legalName', 'Obchodní název')}</Label>
            <Input size='sm' value={form.legal_name} onChange={(e) => set('legal_name', e.target.value)} />
          </div>
          <div>
            <Label>{t('tenantsAccess.ico', 'IČO')}</Label>
            <Input size='sm' className='font-mono' value={form.ico} onChange={(e) => set('ico', e.target.value)} />
          </div>
          <div>
            <Label>{t('tenantsAccess.dic', 'DIČ')}</Label>
            <Input size='sm' className='font-mono' value={form.dic} onChange={(e) => set('dic', e.target.value)} />
          </div>
          <div className='sm:col-span-2'>
            <Label>{t('tenantsAccess.address', 'Adresa')}</Label>
            <Input size='sm' value={form.address} onChange={(e) => set('address', e.target.value)} />
          </div>
          <div>
            <Label>{t('tenantsAccess.city', 'Město')}</Label>
            <Input size='sm' value={form.city} onChange={(e) => set('city', e.target.value)} />
          </div>
          <div>
            <Label>{t('tenantsAccess.zip', 'PSČ')}</Label>
            <Input size='sm' value={form.zip} onChange={(e) => set('zip', e.target.value)} />
          </div>
          <div>
            <Label>{t('tenantsAccess.country', 'Země')}</Label>
            <Input size='sm' value={form.country} onChange={(e) => set('country', e.target.value)} placeholder='CZ' />
          </div>
          <div>
            <Label>Web</Label>
            <Input size='sm' value={form.website} onChange={(e) => set('website', e.target.value)} placeholder='https://' />
          </div>
        </div>

        <div className='border-t border-gray-800 pt-3 space-y-3'>
          <div className='text-xs font-medium text-gray-400 flex items-center gap-1'>
            <Mail size={12} /> {t('tenantsAccess.primaryContact', 'Primární kontakt')}
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
            <div>
              <Label>{t('tenantsAccess.contactName', 'Jméno')}</Label>
              <Input size='sm' value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} />
            </div>
            <div>
              <Label>E-mail</Label>
              <Input size='sm' type='email' value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} />
            </div>
            <div>
              <Label><Phone size={10} className='inline' /> {t('tenantsAccess.phone', 'Telefon')}</Label>
              <Input size='sm' value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} />
            </div>
          </div>
        </div>

        <div className='border-t border-gray-800 pt-3 space-y-3'>
          <div className='text-xs font-medium text-gray-400'>
            {t('tenantsAccess.techContact', 'Technický kontakt')}
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
            <div>
              <Label>{t('tenantsAccess.contactName', 'Jméno')}</Label>
              <Input size='sm' value={form.tech_name} onChange={(e) => set('tech_name', e.target.value)} />
            </div>
            <div>
              <Label>E-mail</Label>
              <Input size='sm' type='email' value={form.tech_email} onChange={(e) => set('tech_email', e.target.value)} />
            </div>
            <div>
              <Label>{t('tenantsAccess.phone', 'Telefon')}</Label>
              <Input size='sm' value={form.tech_phone} onChange={(e) => set('tech_phone', e.target.value)} />
            </div>
          </div>
          <div>
            <Label>{t('tenantsAccess.billingEmail', 'Fakturační e-mail')}</Label>
            <Input size='sm' type='email' value={form.billing_email} onChange={(e) => set('billing_email', e.target.value)} />
          </div>
        </div>

        <div className='border-t border-gray-800 pt-3 space-y-3'>
          <div className='text-xs font-medium text-gray-400'>
            {t('tenantsAccess.helpdeskSection', 'Helpdesk (příprava)')}
          </div>
          <p className='text-[11px] text-gray-600'>
            {t(
              'tenantsAccess.helpdeskHint',
              'Později propojení s helpdeskem hostovaným na ACM nodu. Ulož odkaz a klíč projektu teď.',
            )}
          </p>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
            <div>
              <Label>{t('tenantsAccess.helpdeskNode', 'ACM node (host helpdesku)')}</Label>
              <Input
                size='sm'
                className='font-mono'
                value={form.helpdesk_node_id}
                onChange={(e) => set('helpdesk_node_id', e.target.value)}
                placeholder='acm01'
              />
            </div>
            <div>
              <Label>{t('tenantsAccess.helpdeskProject', 'Project key')}</Label>
              <Input
                size='sm'
                className='font-mono'
                value={form.helpdesk_project_key}
                onChange={(e) => set('helpdesk_project_key', e.target.value)}
                placeholder='SUP'
              />
            </div>
            <div className='sm:col-span-2'>
              <Label>URL</Label>
              <Input
                size='sm'
                value={form.helpdesk_url}
                onChange={(e) => set('helpdesk_url', e.target.value)}
                placeholder='https://helpdesk…'
              />
            </div>
            <div className='sm:col-span-2'>
              <Label>{t('tenantsAccess.helpdeskExtId', 'Externí ID zákazníka')}</Label>
              <Input
                size='sm'
                className='font-mono'
                value={form.helpdesk_external_id}
                onChange={(e) => set('helpdesk_external_id', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div>
          <Label>{t('tenantsAccess.notes', 'Poznámky')}</Label>
          <Input size='sm' value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </div>
      </CardContent>
    </Card>
  );
}

function TenantDetailPanel(
  { tenant, summary }: {
    tenant: TenantFlat;
    summary?: { group_count: number; member_count: number; key_count: number };
  },
) {
  const { t } = useTranslation();
  // Groups for this tenant
  const { data: groupsData } = useQuery({
    queryKey: ['tenant-groups', tenant.id],
    queryFn: () => mcpCall<{ ok: boolean; groups: GroupRow[] }>('identity.group_list', { tenant_id: tenant.id }),
  });
  const groups = (groupsData?.groups ?? []).filter((g) => g.tenant_id === tenant.id);

  // Nodes
  const { data: nodesData } = useQuery({
    queryKey: ['tenant-nodes', tenant.slug],
    queryFn: () => mcpCall<{ ok: boolean; nodes: Array<{ node_id: string; hostname: string; status: string }> }>(
      'mesh.nodes', { action: 'list', tenant_slug: tenant.slug },
    ),
  });
  const tenantNodes = (nodesData?.nodes ?? []).filter((n: Record<string, unknown>) =>
    (n as { tenant_id?: string }).tenant_id === tenant.id
  );

  // Modules
  const { data: modulesData } = useQuery({
    queryKey: ['tenant-modules', tenant.slug],
    queryFn: () => mcpCall<{ ok: boolean; modules: string[] }>('mesh.tenants', {
      action: 'modules', slug: tenant.slug,
    }),
  });
  const modules = modulesData?.modules ?? [];

  // Členové = entity s entities.tenant_id (přímé vlastnictví, ne group membership).
  // MCP-first (identity.entity_list).
  const { data: membersData } = useQuery({
    queryKey: ['tenant-members', tenant.id],
    queryFn: () =>
      mcpCall<{ ok: boolean; entities: TenantMember[] }>('identity.entity_list', {
        tenant_id: tenant.id,
      }),
  });
  const members = membersData?.entities ?? [];

  return (
    <>
      {/* Header */}
      <Card>
        <CardContent className='py-3 flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <Users size={18} className='text-blue-400' />
            <div>
              <span className='text-sm font-bold text-white'>{tenant.name}</span>
              <span className='ml-2 text-[10px] text-gray-500 font-mono'>{tenant.slug}</span>
              <Badge className='ml-2 text-[9px]'>{tenant.plan}</Badge>
            </div>
          </div>
          <div className='flex gap-4 text-[10px] text-gray-500'>
            <span>
              <Users size={11} className='inline' /> {t('tenantsAccess.groupsCount', { count: summary?.group_count ?? 0 })}
            </span>
            <span>
              <Key size={11} className='inline' /> {t('tenantsAccess.keysCount', { count: summary?.key_count ?? 0 })}
            </span>
            <span>
              <Server size={11} className='inline' /> {t('tenantsAccess.nodesCount', { count: tenantNodes.length })}
            </span>
          </div>
        </CardContent>
      </Card>

      <TenantProfileCard tenant={tenant} />

      {/* Členové — entity vlastněné tenantem */}
      <Card>
        <CardHeader>
          <CardTitle className='text-sm'>
            <Users size={14} className='inline mr-1' /> {t('tenantsAccess.membersTitle', { count: members.length })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {members.length === 0
            ? (
              <p className='text-xs text-gray-500'>
                {t('tenantsAccess.noEntitiesHint')}
              </p>
            )
            : (
              <div className='space-y-1'>
                {members.map((m) => (
                  <div
                    key={m.id}
                    className='flex items-center gap-2.5 p-2 bg-gray-900/40 rounded-lg text-xs'
                  >
                    <EntityAvatar
                      label={m.label}
                      avatar={(m.metadata?.avatar as string) ?? null}
                      size={24}
                    />
                    <span className='text-white font-medium flex-1 truncate'>{m.label}</span>
                    <Badge variant={m.type === 'user' ? 'blue' : 'gray'}>{m.type}</Badge>
                    {!m.enabled && <Badge variant='red'>{t('tenantsAccess.disabledBadge')}</Badge>}
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      {/* Skupiny */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center justify-between text-sm'>
            <span>
              <Users size={14} className='inline mr-1' /> {t('tenantsAccess.groupsTitle', { count: groups.length })}
            </span>
            <Button
              size='sm'
              variant='primary'
              icon={<Plus size={12} />}
              onClick={() =>
                toast.info(t('tenantsAccess.createGroupHint', { name: tenant.name }))}
            >
              {t('tenantsAccess.newGroup')}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {groups.length === 0
            ? <p className='text-xs text-gray-500'>{t('tenantsAccess.noGroups')}</p>
            : (
              <div className='space-y-1'>
                {groups.map((g) => (
                  <div
                    key={g.id}
                    className='flex items-center justify-between p-2 bg-gray-900/40 rounded text-xs'
                  >
                    <div>
                      <span className='text-white font-medium'>{g.name}</span>
                      {(g.metadata?.acm_role as string) && (
                        <span className='ml-2 text-[10px] text-red-400 bg-red-500/10 px-1 rounded'>
                          {g.metadata.acm_role as string}
                        </span>
                      )}
                    </div>
                    <span className='text-gray-500'>{t('tenantsAccess.membersCount', { count: g.member_count })}</span>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      {/* Prostředky */}
      <Card>
        <CardHeader>
          <CardTitle className='text-sm'>
            <Server size={14} className='inline mr-1' /> {t('tenantsAccess.resourcesTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <div>
            <span className='text-[10px] text-gray-400 font-medium'>
              {t('tenantsAccess.nodesTitle', { count: tenantNodes.length })}
            </span>
            <div className='flex flex-wrap gap-1 mt-1'>
              {tenantNodes.map((n: Record<string, unknown>) => (
                <Badge
                  key={n.node_id as string}
                  variant={(n.status as string) === 'online' ? 'green' : 'gray'}
                >
                  {(n.hostname as string) || (n.node_id as string)}
                </Badge>
              ))}
              {tenantNodes.length === 0 && <span className='text-xs text-gray-600'>{t('tenantsAccess.none')}</span>}
            </div>
          </div>
          <div>
            <span className='text-[10px] text-gray-400 font-medium'>{t('tenantsAccess.modulesTitle', { count: modules.length })}</span>
            <div className='flex flex-wrap gap-1 mt-1'>
              {modules.map((m) => <Badge key={m}>{m}</Badge>)}
              {modules.length === 0 && <span className='text-xs text-gray-600'>{t('tenantsAccess.none')}</span>}
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
